import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { Button } from 'primereact/button';
import { Calendar, type CalendarChangeEvent } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import React, { useState } from 'react';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault('Europe/London');

interface IDateRangePickerDialogProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  selectedDates: Date[] | null;
  onSubmit: (dates: Date[] | null) => Promise<void> | void;
  title?: string;
}

const DateRangePickerDialog = ({
  visible,
  setVisible,
  selectedDates: _selectedDates,
  onSubmit,
  title = 'Edit selected date',
}: IDateRangePickerDialogProps) => {
  const [selectedDates, setSelectedDates] = useState<Date[] | null>(
    () => _selectedDates,
  );
  const [pending, setPending] = useState(() => false);

  React.useEffect(() => {
    setSelectedDates(_selectedDates ?? []);
  }, [_selectedDates]);

  function handleCloseDialog() {
    setVisible(false);
  }

  function handleSubmit() {
    (async () => {
      setPending(true);
      try {
        await onSubmit(selectedDates);
        setPending(false);
        handleCloseDialog();
      } catch {
        setPending(false);
      }
    })().catch(() => {});
  }

  function onChangeSelectedDate(e: CalendarChangeEvent) {
    if (
      e.value != null &&
      !Array.isArray(e.value) &&
      typeof e.value !== 'string'
    ) {
      setSelectedDates([e.value]);
    }
  }

  function renderFooter() {
    return (
      <React.Fragment>
        <Button
          label="Discard"
          icon="pi pi-times"
          className="p-button-text p-button-secondary"
          onClick={handleCloseDialog}
          disabled={pending}
        />
        <Button
          label="Submit"
          icon="pi pi-check"
          className="p-button-success"
          onClick={handleSubmit}
          loading={pending}
          disabled={pending}
        />
      </React.Fragment>
    );
  }

  return (
    <Dialog
      className="w-6/12"
      header={title}
      visible={visible}
      modal
      onHide={handleCloseDialog}
      footer={renderFooter}
    >
      <Calendar
        className="w-full"
        value={selectedDates}
        onChange={onChangeSelectedDate}
        locale="en"
        selectionMode="range"
        inline
      />
    </Dialog>
  );
};

export default DateRangePickerDialog;
